/**
 * Tabla de la librería de UI (Tailwind, estilos del proyecto).
 *
 * Piezas presentacionales sobre los elementos nativos (`<table>`, `<thead>`,
 * `<tbody>`, `<tr>`, `<th>`, `<td>`), que ya aportan la semántica accesible
 * (rol `table`/`row`/`columnheader`/`cell`). Sin lógica de negocio ni datos:
 * las pantallas pasan las filas. `Table` se envuelve en un contenedor con scroll
 * horizontal para no romper el layout en pantallas estrechas.
 */
import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";

import { cn } from "./cn";

export const Table = forwardRef<
  HTMLTableElement,
  HTMLAttributes<HTMLTableElement>
>(function Table({ className, ...props }, ref) {
  return (
    <div className="w-full overflow-x-auto">
      <table
        ref={ref}
        className={cn("w-full border-collapse text-left text-sm", className)}
        {...props}
      />
    </div>
  );
});

export const TableHead = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableHead({ className, ...props }, ref) {
  return (
    <thead
      ref={ref}
      className={cn("border-b border-slate-200 bg-slate-50", className)}
      {...props}
    />
  );
});

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, ...props }, ref) {
  return (
    <tbody
      ref={ref}
      className={cn("divide-y divide-slate-100", className)}
      {...props}
    />
  );
});

export const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(function TableRow({ className, ...props }, ref) {
  return (
    <tr ref={ref} className={cn("hover:bg-slate-50", className)} {...props} />
  );
});

export const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(function TableHeaderCell({ className, scope = "col", ...props }, ref) {
  return (
    <th
      ref={ref}
      scope={scope}
      className={cn(
        "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500",
        className,
      )}
      {...props}
    />
  );
});

export const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(function TableCell({ className, ...props }, ref) {
  return (
    <td ref={ref} className={cn("px-4 py-2.5 text-slate-700", className)} {...props} />
  );
});
